import classNames from 'classnames';
import React from 'react';
import {connect} from 'react-redux';

import navigateToURL from 'lib/navigateToURL';
import {getPartyDisplayString} from 'parties/GetPartyDisplayString';
import {BenchlingState} from 'redux-stores/reduceApp';
import {BenchlingDispatch} from 'redux-stores/Store';
import {AssaySchema} from 'results/assay-schemas/AssaySchemaConstants';
import {AssayRun} from 'results/assays/AssayRecordConstants';
import {AssayRecordField} from 'results/assays/AssayRecordRendering';
import {getTagForField} from 'results/assays/getTagForField';
import {MODEL_NAMES} from 'results/ResultsNamingConstants';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {getEmptyFilterErrors} from 'search/filters/ValidateFilters';
import {Filter, FilterKey, SingleValueFilter} from 'search/SearchConstants';
import {sanitizeFilters} from 'search/SearchUtil';
import getTestProps from 'test/lib/cypress/getTestProps';
import {Cell, Column, Table} from 'ui-library/simple-table/SimpleTable';
import * as DateUtil from 'util/DateUtil';

import {AvailabilityStatus, EntriesByRunId} from './RunsInboxModalConstants';
import {fetchMoreAssayRuns, selectRun} from './RunsInboxModalReduxActions';

interface ConnectedProps {
  assayRuns: Array<AssayRun>;
  canFetchMore: boolean;
  entriesByRunId: EntriesByRunId;
  isLoadingMore: boolean;
  selectedRun: AssayRun | null;
  filters: Array<Filter>;
}

interface OwnProps {
  assayRunSchema: AssaySchema;
}

interface DispatchProps {
  onLoadMoreRuns: (schemaId: string, filters: Array<Filter>) => void;
  onSelectRun: (runId: string) => void;
}

export type RunsInboxTableProps = ConnectedProps & OwnProps & DispatchProps;

function mapStateToProps(state: BenchlingState, props: OwnProps): ConnectedProps {
  const assayRunsById = state.models.results.assayRunsById as {
    [runId: string]: AssayRun;
  };
  const {assayRunIds, entriesByRunId, isLoadingMore, offset, total, selectedRunId, filters} =
    state.ui.results.runsInboxModal;

  const assayRuns = assayRunIds.map((assayRunId: string) => assayRunsById[assayRunId]);

  const canFetchMore = offset < total;
  const selectedRun = selectedRunId ? assayRunsById[selectedRunId] : null;

  return {
    assayRuns,
    entriesByRunId,
    canFetchMore,
    isLoadingMore,
    selectedRun,
    filters,
  };
}

function mapDispatchToProps(dispatch: BenchlingDispatch): DispatchProps {
  return {
    onLoadMoreRuns: (schemaId: string, filters: Array<Filter>) => {
      dispatch(fetchMoreAssayRuns(schemaId, filters));
    },
    onSelectRun: (runId: string) => {
      dispatch(selectRun(runId));
    },
  };
}

interface InboxCellProps {
  rowIndex: number;
  faded: boolean;
  selectRunFromRow: (rowIndex: number) => void;
  className?: string;
  children: JSX.Element | string | undefined;
}
function InboxCell(props: InboxCellProps): JSX.Element {
  return (
    <Cell
      onClick={() => {
        props.selectRunFromRow(props.rowIndex);
      }}
      className={classNames(props.className, {
        'u-faded': props.faded,
      })}
    >
      {props.children}
    </Cell>
  );
}

/**
 * The component that displays assay runs in a table.
 *
 *
 */
export class RunsInboxTable extends React.Component<RunsInboxTableProps> {
  _getAssayRunIdFromRow = (rowIndex: number): string => this.props.assayRuns[rowIndex].id;

  _getStatusFilter(): AvailabilityStatus {
    return (
      this.props.filters.find((filter) => filter.key === FilterKey.RUN_AVAILABILITY) as SingleValueFilter
    ).value as AvailabilityStatus;
  }

  _selectRunFromRow = (rowIndex: number): void => {
    if (this._getStatusFilter() === AvailabilityStatus.AVAILABLE) {
      this.props.onSelectRun(this._getAssayRunIdFromRow(rowIndex));
    }
  };

  _getSharedCellProps(rowIndex: number): {faded: boolean; selectRunFromRow: (rowIndex: number) => void} {
    return {
      faded: this.props.assayRuns[rowIndex].archiveRecord != null,
      selectRunFromRow: this._selectRunFromRow,
    };
  }

  override render(): JSX.Element {
    const statusFilter = this._getStatusFilter();
    return (
      <div className="runsInboxModal-table">
        <Table
          emptyTableText={`No ${MODEL_NAMES.runs}`}
          numRows={this.props.assayRuns.length}
          // Show the scrollbar in the parent div instead of the SimpleTable (BNCH-1364)
          overflowVisible={true}
        >
          <Column
            header={<Cell className="simpleTable-checkboxCell" />}
            renderCell={(rowIndex: number) => (
              <InboxCell
                {...this._getSharedCellProps(rowIndex)}
                rowIndex={rowIndex}
                className="simpleTable-checkboxCell"
              >
                <input
                  type="radio"
                  checked={this._getAssayRunIdFromRow(rowIndex) === this.props.selectedRun?.id}
                  onChange={() => {}} // React warns if not set -- we don't need since we update on click
                  {...getTestProps({component: 'RunsInboxTable', element: 'radioButton'})}
                />
              </InboxCell>
            )}
          />
          <Column
            header="Creator"
            renderCell={(rowIndex: number) => (
              <InboxCell {...this._getSharedCellProps(rowIndex)} rowIndex={rowIndex}>
                {getPartyDisplayString(this.props.assayRuns[rowIndex].creator)}
              </InboxCell>
            )}
          />
          <Column
            header="Created At"
            renderCell={(rowIndex: number) => (
              <InboxCell {...this._getSharedCellProps(rowIndex)} rowIndex={rowIndex}>
                {DateUtil.formalTime(this.props.assayRuns[rowIndex].createdAt)}
              </InboxCell>
            )}
          />
          {statusFilter === AvailabilityStatus.IN_ENTRY && (
            <Column
              header="Entry"
              renderCell={(rowIndex: number) => (
                <InboxCell {...this._getSharedCellProps(rowIndex)} rowIndex={rowIndex}>
                  <EntryLink
                    assayRun={this.props.assayRuns[rowIndex]}
                    entriesByRunId={this.props.entriesByRunId}
                  />
                </InboxCell>
              )}
            />
          )}
          {this.props.assayRunSchema.fields.map((field: SchemaField, i: number) => (
            <Column
              key={field.name}
              header={field.displayName}
              renderCell={(rowIndex: number) => (
                <InboxCell {...this._getSharedCellProps(rowIndex)} rowIndex={rowIndex}>
                  <AssayRecordField
                    tag={getTagForField(this.props.assayRuns[rowIndex], field.id)}
                    shouldPreviewImages={false}
                  />
                </InboxCell>
              )}
            />
          ))}
        </Table>
        {this.props.canFetchMore && (
          <LoadMoreRunsButton
            isLoadingMore={this.props.isLoadingMore}
            onLoadMore={() => {
              const filters = sanitizeFilters(this.props.filters, getEmptyFilterErrors(this.props.filters));
              this.props.onLoadMoreRuns(this.props.assayRunSchema.id, filters);
            }}
          />
        )}
      </div>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(RunsInboxTable);

interface EntryLinkProps {
  assayRun: AssayRun;
  entriesByRunId: EntriesByRunId;
}

function EntryLink(props: EntryLinkProps): JSX.Element {
  const entry = props.entriesByRunId[props.assayRun.id];
  return (
    <a
      className="u-ellipsis-text"
      onClick={() => {
        navigateToURL(entry.editURL);
      }}
    >
      {entry.name}
    </a>
  );
}

interface LoadMoreRunsButtonProps {
  isLoadingMore: boolean;
  onLoadMore: () => void;
}

function LoadMoreRunsButton(props: LoadMoreRunsButtonProps): JSX.Element {
  return (
    <div className="u-text-align-center u-small-spacing">
      {props.isLoadingMore ? (
        <span className="u-faded">Loading...</span>
      ) : (
        <a onClick={props.onLoadMore}>Load more {MODEL_NAMES.runs}</a>
      )}
    </div>
  );
}
